import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Trophy, BookOpen, UserCheck,
  CheckCircle, ArrowRight, ArrowLeft, Star, Gift,
  ChevronDown, ChevronUp, Dumbbell, Utensils, Heart,
  TrendingUp, Calendar, Award, MessageCircle, Zap, Users,
} from 'lucide-react';

/*
 * SalesPage — light editorial redesign (Feb 2026)
 * ----------------------------------------------
 * Deliberately overrides the app's global dark theme with hardcoded stone/white
 * Tailwind classes so the rest of the (still-dark) app is untouched. Palette:
 *   • Charcoal   — stone-900 for accent sections
 *   • Off-white  — stone-50 dominant, white for airy sections
 *   • Warm beige — #F0EBE1 for editorial callouts
 *   • Brand yellow — #EFB342 as a considered accent, never a background wash
 *
 * People-first: every major section anchors on lifestyle photography of women
 * 40–60 (hiking, lifting, cooking, journaling, socializing). Photo URLs are
 * curated Unsplash/Pexels placeholders — the coach will swap in her own real
 * photos + personal portraits later.
 */

// Curated placeholder photography — swap for real photos later
const PHOTOS = {
  hero: 'https://customer-assets-eiarnc6j.emergentagent.net/job_39ce99f8-2df7-4a5a-9cad-1b3f94f84fe9/artifacts/2f0px4k4_image.png',
  heroSecondary: 'https://customer-assets-eiarnc6j.emergentagent.net/job_39ce99f8-2df7-4a5a-9cad-1b3f94f84fe9/artifacts/r3w5tdha_image.png',
  pillar1: 'https://customer-assets-eiarnc6j.emergentagent.net/job_39ce99f8-2df7-4a5a-9cad-1b3f94f84fe9/artifacts/h12vdfw6_blond%20lady%20reg%20size.jpg',
  pillar2: 'https://customer-assets-eiarnc6j.emergentagent.net/job_39ce99f8-2df7-4a5a-9cad-1b3f94f84fe9/artifacts/uk1xttyd_image.png',
  pillar3: 'https://images.unsplash.com/photo-1642543492517-a45bb79aaaf3?crop=entropy&cs=srgb&fm=jpg&q=85&w=900',
  social: 'https://images.unsplash.com/photo-1519671282429-b44660ead0a7?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400',
  practices: 'https://images.unsplash.com/photo-1490645935967-10de6ba17061?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400',
  coach: 'https://customer-assets-eiarnc6j.emergentagent.net/job_39ce99f8-2df7-4a5a-9cad-1b3f94f84fe9/artifacts/ho59sw0f_image.png',
};

const SalesPage = () => {
  const [openFaq, setOpenFaq] = useState(null);
  const [expandedActions, setExpandedActions] = useState({});

  const challengeActions = [
    { id: 1, name: 'Limit Alcohol', emoji: '🍷', description: 'Empty calories that slow metabolism, disrupt sleep, interfere with muscle recovery & throw off hormone balance.' },
    { id: 2, name: 'Meal Plan & Prep', emoji: '📋', description: 'Proven to dramatically increase diet success! Reduces decision fatigue, controls portions, avoids impulsive choices.' },
    { id: 3, name: '30 Min Outside Activity', emoji: '🚶', description: 'Walking, gardening, etc. NEAT can burn 3.5x more calories than gym workouts! Aim for 10k+ steps. Fresh air & sunshine boost mood.' },
    { id: 4, name: '7+ Hours Sleep', emoji: '😴', description: 'Optimal energy, mental focus, hunger hormone regulation, insulin sensitivity, reduced cortisol & muscle repair during deep sleep.' },
    { id: 5, name: 'Hit Protein Target', emoji: '🥩', description: '1g protein per lb of goal bodyweight. Increases satiety, preserves muscle, boosts metabolism.' },
    { id: 6, name: '2+ Cups Veggies', emoji: '🥗', description: 'Essential micronutrients, low calorie, high fiber to support digestion, blood sugar control & cellular health.' },
    { id: 7, name: 'Limit Refined Sugar', emoji: '🚫', description: 'Skip the empty calories that spike blood sugar, increase inflammation & disrupt hunger signals and hormones.' },
    { id: 8, name: '2+ Litres Water', emoji: '💧', description: 'Drinking 500ml of water increases metabolic rate by 30%! More on training days. Stay hydrated for optimal function.' },
    { id: 9, name: 'Balanced Dinner + 1 Snack Max', emoji: '🍽️', description: 'Spread energy intake throughout the day to avoid blood sugar crashes, stress hormones & evening overeating.' },
    { id: 10, name: 'Strength Training', emoji: '💪', description: '4-6x/week boosts metabolism & body composition. Muscle increases resting calorie burn & improves insulin sensitivity.' },
  ];

  const toggleAction = (id) => {
    setExpandedActions((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  // SEO Meta Tags
  useEffect(() => {
    document.title = 'RxGRIT | Daily Menopause Coaching by BUILD Nutrition';
    const descriptionContent = 'Master Your Hormones: Menopause Metabolism App';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', descriptionContent);
    } else {
      const m = document.createElement('meta');
      m.name = 'description';
      m.content = descriptionContent;
      document.head.appendChild(m);
    }
    const ogTags = {
      'og:title': 'RxGRIT | Daily Menopause Coaching by BUILD Nutrition',
      'og:description': descriptionContent,
      'og:type': 'website',
      'og:url': window.location.href,
      'og:image': 'https://rxgrit-health-1.emergent.host/og-share-hikers.jpg',
      'og:site_name': 'RxGRIT by BUILD Nutrition',
    };
    Object.entries(ogTags).forEach(([property, content]) => {
      let tag = document.querySelector(`meta[property="${property}"]`);
      if (tag) {
        tag.setAttribute('content', content);
      } else {
        tag = document.createElement('meta');
        tag.setAttribute('property', property);
        tag.content = content;
        document.head.appendChild(tag);
      }
    });
    return () => { document.title = 'RxGRIT by BUILD Nutrition'; };
  }, []);

  const faqs = [
    { question: 'What are the 10 daily practices?', answer: "They're simple, proven habits that boost metabolism, build muscle, burn fat, and supercharge your energy. No extremes, no restrictions - just real results. Things like drinking enough H2O, eating more veggies, hitting your protein targets, cutting out the sugar. The best part - you get to decide what you'll shoot for each month. The challenge repeats every month because consistency always wins over perfection." },
    { question: 'Is RxGRIT just another diet or weight-loss program?', answer: 'No. RxGRIT is designed to help you understand the physiological changes of menopause and turn that knowledge into practical daily habits. Weight loss may be one goal, but the focus is on the things you can actually influence — nutrition, muscle building, sleep & recovery, insulin and blood sugar regulation, and lifestyle factors that support your changing biology.' },
    { question: 'Do I have to track my progress?', answer: "Tracking your points is optional, but highly recommended! Research shows that recording and awarding yourself points for taking action significantly improves your compliance - and that's how you build winning habits! You can use the app, or even a paper tracker if you're old school. But it's absolutely not required - RxGRIT makes it easy for you to decide." },
    { question: "What if I'm not super techy or into tracking everything?", answer: 'Perfect — RxGRIT is built to be low-fuss and completely autonomous. You get to decide whether you\'ll track points, do your own macro-tracking, follow one of our sample meal plans, or whatever else! No strict rules - everything we provide is completely optional. Pop in and out as it pleases you!' },
    { question: 'Will this help me lose weight or build muscle?', answer: 'Absolutely. The 10 daily practices are the key drivers of optimal metabolism, hormones, and energy production - all crucial to fat loss, muscle growth and repair, and optimal health overall. You can also learn how to calculate and adjust your own macros using our Guidebooks, and take advantage of half-price 1:1 coaching anytime.' },
    { question: 'How do I access everything?', answer: "Once you sign up, you'll get immediate access to the app with the monthly challenges and leaderboard, the private members-only Toolbox with the full library of resources, your member discount code for 1:1 sessions, and all the info you need to get started!" },
    { question: 'Can I get personalized help if I need it?', answer: "Yes! Members get half-price coaching sessions anytime you want personalized support. Once you sign up, you'll receive your exclusive discount code and booking link for your half-priced 1:1 coaching sessions!" },
  ];

  const testimonials = [
    { quote: "Worth every penny. I love the accountability and I've already booked a 1:1 to dial things in.", name: 'Shawna R.' },
    { quote: 'The monthly challenges help me reset without obsessing. I actually enjoy the process now.', name: 'Katharine K.' },
    { quote: "My deep mid-day crashes have been replaced with 'joie de vivre'. I feel like a new woman!", name: 'Nancy G.' },
  ];

  return (
    <div className="min-h-screen bg-stone-50 text-stone-900" data-testid="sales-page">
      {/* Top Banner */}
      <div className="fixed top-0 left-0 right-0 z-[60] bg-stone-200 border-b border-stone-300">
        <div className="max-w-6xl mx-auto px-4">
          <a
            href="https://buildnutrition.ca/"
            className="flex items-center gap-1.5 py-2 text-stone-700 hover:text-stone-900 transition-colors text-sm font-medium w-fit"
            data-testid="back-to-build-nutrition-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Build Nutrition Home Page
          </a>
        </div>
      </div>

      {/* Navigation */}
      <nav className="fixed top-[36px] left-0 right-0 z-50 bg-[#F5F4F1]/90 backdrop-blur-xl border-b border-stone-200">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="nav-logo-link">
            <div className="w-10 h-10 bg-[#EFB342] rounded-lg flex items-center justify-center">
              <span className="text-black font-heading font-bold text-xl">Rx</span>
            </div>
            <div>
              <h1 className="font-heading text-xl font-bold text-stone-900 tracking-wide">RxGRIT</h1>
              <p className="text-[9px] text-stone-500 uppercase tracking-widest">by BUILD Nutrition</p>
            </div>
          </Link>
          <div className="flex items-center gap-5">
            <Link to="/login" className="text-stone-700 hover:text-stone-900 transition-colors text-sm font-semibold hidden sm:block" data-testid="nav-login-link">
              Member Login
            </Link>
            <a
              href="https://l.bttr.to/fXYvA"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-[#EFB342] text-black px-5 py-2 rounded-full font-semibold text-sm hover:bg-[#D99F30] transition-colors"
              data-testid="nav-join-btn"
            >
              Join Now
            </a>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-40 md:pt-44 pb-24 px-4 bg-stone-50">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 md:gap-16 items-center">
          <div className="order-2 md:order-1">
            <p className="text-[#EFB342] uppercase tracking-[0.2em] text-xs font-semibold mb-5" data-testid="hero-overline">
              POWERED BY BUILD NUTRITION
            </p>
            <h1 className="font-heading text-5xl font-bold text-stone-900 mb-6 leading-[1] tracking-tight uppercase">
              <span className="block">Tackling menopause</span>
              <span className="block">takes grit.</span>
              <span className="block text-base md:text-xl lg:text-2xl font-semibold text-stone-600 mt-4 leading-[1.2] tracking-normal">
                Build yours here.
              </span>
            </h1>
            <p className="text-sm md:text-base text-stone-700 mb-9 leading-relaxed max-w-lg">
              RxGRIT is your daily menopause education and support system — delivered through a fun, engaging app that helps you turn what you learn into habits you can actually use.
            </p>

            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <a
                href="https://l.bttr.to/fXYvA"
                target="_blank"
                rel="noopener noreferrer"
                className="group bg-[#EFB342] text-black px-8 py-4 rounded-full font-semibold text-base hover:bg-[#D99F30] transition-all hover:shadow-lg hover:-translate-y-0.5 flex items-center gap-2 w-full sm:w-auto justify-center"
                data-testid="hero-primary-cta"
              >
                Start Building Your GRIT
                <ArrowRight size={18} className="group-hover:translate-x-0.5 transition-transform" />
              </a>
              <Link
                to="/free-trial"
                className="bg-[#F5F4F1] border border-stone-300 text-stone-800 px-8 py-4 rounded-full font-semibold text-base hover:bg-stone-100 transition-all flex items-center gap-2 w-full sm:w-auto justify-center"
                data-testid="hero-secondary-cta"
              >
                <Gift size={18} />
                Try 7 Days Free
              </Link>
            </div>
          </div>

          {/* Right — layered lifestyle photography */}
          <div className="order-1 md:order-2 relative">
            <div className="relative aspect-square rounded-3xl overflow-hidden shadow-xl">
              <img
                src={PHOTOS.hero}
                alt="Women hiking in nature — active lifestyle at 40+"
                className="w-full h-full object-cover"
                data-testid="hero-image-primary"
              />
            </div>
            <div className="hidden md:block absolute -bottom-8 -left-8 w-48 lg:w-56 aspect-square rounded-2xl overflow-hidden shadow-xl border-4 border-stone-50">
              <img
                src={PHOTOS.heroSecondary}
                alt="Woman preparing real, whole food in her kitchen"
                className="w-full h-full object-cover"
                data-testid="hero-image-secondary"
              />
            </div>
            <div className="hidden md:block absolute -top-6 -right-4 w-32 lg:w-36 bg-[#F5F4F1] rounded-2xl shadow-lg px-5 py-4 border border-stone-100">
              <p className="font-heading text-3xl font-bold text-[#EFB342] leading-none mb-1">Daily</p>
              <p className="text-xs text-stone-600 leading-tight">Education · Action · Accountability</p>
            </div>
          </div>
        </div>
      </section>

      {/* 7-Day Free Trial */}
      <section
        className="py-20 md:py-24 px-4 border-y border-stone-800 relative bg-stone-900"
        style={{
          backgroundImage:
            'linear-gradient(rgba(30, 22, 14, 0.62), rgba(30, 22, 14, 0.58)), url(/free_trial_coffee_app.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="max-w-6xl mx-auto">
          <div className="max-w-xl text-left md:pl-4 lg:pl-8">
            <h3 className="font-heading text-2xl md:text-3xl font-bold text-white mb-4">
              Try RxGRIT free for 7 days
            </h3>
            <p className="text-stone-200 text-sm md:text-base mb-8 leading-relaxed">
              Midlife hormone shifts bring changes you didn&apos;t ask for. But you can tackle them with knowledge, consistency, and perseverance — that&apos;s GRIT in action.
            </p>
            <Link
              to="/free-trial"
              className="inline-flex items-center gap-2 bg-[#EFB342] text-black px-10 py-4 md:px-12 md:py-5 rounded-full font-bold text-base md:text-lg hover:bg-[#D99F30] transition-all hover:-translate-y-0.5 hover:shadow-lg uppercase tracking-wide"
              data-testid="countdown-cta"
            >
              I&apos;m in! Start my 7 days
              <ArrowRight size={20} />
            </Link>
            <p className="text-stone-300 text-sm mt-4 italic">No credit card required. No commitment.</p>
          </div>
        </div>
      </section>

      {/* THE PROBLEM — knowing isn't doing */}
      <section className="py-24 px-4 bg-[#F5F4F1]">
        <div className="max-w-4xl mx-auto">
          <p className="text-[#EFB342] uppercase tracking-[0.2em] text-xs font-semibold text-center mb-4">The problem</p>
          <h2 className="font-heading text-2xl md:text-4xl font-bold text-stone-900 text-center mb-6 leading-tight">
            Hormone shifts really do change your entire biology.
          </h2>
          <div className="text-stone-600 text-sm md:text-base mb-12 max-w-2xl mx-auto leading-relaxed space-y-3">
            <p><span className="font-semibold text-stone-800">You may not be doing anything differently. But your body sure is.</span></p>
            <p>You&apos;re eating well. You&apos;re exercising. You&apos;re doing everything you&apos;ve always done — yet the unwanted changes keep coming anyway.</p>
            <p>So you tighten things up. You eat less. You exercise more. You work harder at doing everything &ldquo;right&rdquo; — and still, your body doesn&apos;t respond the way it used to. If anything, it feels like <span className="text-stone-900 font-semibold">you&apos;re working harder than ever just to stay where you were.</span></p>
            <p className="text-stone-800 font-medium">And it&apos;s incredibly frustrating.</p>
          </div>

          <div className="grid md:grid-cols-2 gap-4 md:gap-5 mb-12">
            {[
              { title: 'Weight gain', body: 'Especially around your middle.' },
              { title: 'Brain fog', body: 'Difficulty concentrating or feeling mentally &ldquo;off.&rdquo;' },
              { title: 'Sleep disruption', body: 'Trouble falling asleep, staying asleep or waking exhausted.' },
              { title: 'Hot flashes & night sweats', body: 'Hot. Cold. Hot. Cold. Repeat.' },
              { title: 'Body composition changes', body: 'Changes in where you carry body fat and muscle.' },
              { title: 'Skin & hair changes', body: 'Drier skin, changes in texture or thinning hair.' },
            ].map((item, index) => (
              <div key={index} className="flex items-start gap-3 bg-stone-50 border border-stone-200 rounded-2xl p-5">
                <CheckCircle className="text-[#EFB342] flex-shrink-0 mt-0.5" size={18} />
                <div>
                  <p className="font-heading font-bold text-stone-900 text-base leading-snug mb-1">{item.title}:</p>
                  <p className="text-stone-700 text-xs md:text-sm leading-relaxed" dangerouslySetInnerHTML={{ __html: item.body }} />
                </div>
              </div>
            ))}
          </div>

          <div className="bg-[#F0EBE1] rounded-3xl p-8 md:p-12 text-center">
            <p className="text-stone-800 text-base md:text-lg leading-relaxed mb-4">
              <span className="font-heading font-bold text-stone-900">You can&apos;t stop menopause from happening. But you don&apos;t have to simply accept every change it brings either.</span>
            </p>
            <p className="text-stone-700 text-sm md:text-base leading-relaxed">
              The first step is understanding the root cause behind these changes — and addressing the things you can.
            </p>
            <p className="text-stone-900 text-sm md:text-base leading-relaxed font-medium mt-6">
              That&apos;s where RxGRIT comes in.
            </p>
          </div>
        </div>
      </section>

      {/* What's Inside — app as delivery mechanism */}
      <section className="py-24 px-4 bg-stone-50">
        <div className="max-w-6xl mx-auto">
          <div className="max-w-2xl mb-14">
            <p className="text-[#EFB342] uppercase tracking-[0.2em] text-xs font-semibold mb-4">Inside RxGRIT</p>
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-stone-900 mb-4 leading-tight uppercase">
              What you get, <span className="text-[#EFB342]">every day.</span>
            </h2>
            <p className="text-stone-600 text-base md:text-lg leading-relaxed">
              Everything inside the RxGRIT app is designed to help you understand what&apos;s happening, take action and build the habits that support your changing biology.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 md:gap-8">
            <PillarCard
              image={PHOTOS.pillar3}
              imageAlt="Woman journaling with coffee, learning and reflecting"
              title="Education"
              description={"Understand what's changing — and why."}
              bullets={[
                { icon: Zap, text: 'Daily GRIT info tips' },
                { icon: TrendingUp, text: 'Menopause & metabolism science' },
                { icon: Heart, text: "Connect the dots between what's changing inside and what you see & feel on the outside" },
              ]}
            />
            <PillarCard
              image={PHOTOS.pillar2}
              imageAlt="Woman preparing whole, colourful food in her home kitchen"
              title="Action"
              description="Turn what you learn into practical strategies you can actually use."
              bullets={[
                { icon: Utensils, text: 'Recipes & meal plans' },
                { icon: Trophy, text: 'Practical daily challenges' },
                { icon: Dumbbell, text: 'Real-life habits' },
              ]}
            />
            <PillarCard
              image={PHOTOS.pillar1}
              imageAlt="Woman with short blond hair checking her phone — real, natural setting"
              imageStyle={{ filter: 'brightness(1.35) saturate(0.9)' }}
              title="Accountability"
              description="Turn healthy habits into a challenge you actually want to win."
              bullets={[
                { icon: Award, text: 'Earn points & compete on the leaderboard each month' },
                { icon: Calendar, text: 'Track your progress and build momentum' },
                { icon: Users, text: 'Community & shared accountability' },
              ]}
            />
          </div>

          {/* Bonus — 1:1 coaching, Yvette's photo makes it feel personal */}
          <div
            className="mt-10 md:mt-14 bg-stone-900 rounded-3xl overflow-hidden grid md:grid-cols-[minmax(0,220px)_1fr] items-stretch shadow-lg"
            data-testid="coaching-callout"
          >
            <div className="relative min-h-[240px] md:min-h-full bg-stone-800">
              <img
                src="https://customer-assets-eiarnc6j.emergentagent.net/job_39ce99f8-2df7-4a5a-9cad-1b3f94f84fe9/artifacts/opgg5s4t_image.png"
                alt="Yvette Styner, RHN — RxGRIT founder and menopause metabolism coach"
                className="absolute inset-0 w-full h-full object-cover object-[68%_20%]"
              />
            </div>
            <div className="p-8 md:p-10 flex flex-col justify-center gap-3 text-center md:text-left">
              <div className="flex items-center justify-center md:justify-start gap-3">
                <span className="inline-block bg-[#EFB342] text-black px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-[0.18em]">
                  Bonus
                </span>
                <p className="text-[#EFB342]/90 uppercase tracking-[0.2em] text-[11px] font-semibold">Included with membership</p>
              </div>
              <h3 className="font-heading text-xl md:text-2xl font-bold text-white leading-tight">
                50% off 1:1 coaching with Yvette — <span className="text-[#EFB342]">whenever you want it.</span>
              </h3>
              <p className="text-stone-300 text-sm md:text-base leading-relaxed">
                Every RxGRIT member gets half-off personalized sessions with me — for personalized questions, nutrition dial-ins, or just when you need a real conversation with a real coach.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 10 Best Practices */}
      <section
        className="py-24 px-4"
        style={{
          backgroundImage: `linear-gradient(rgba(30,22,14,0.62), rgba(30,22,14,0.58)), url("/best_practices_gym_bg.png")`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        }}
      >
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14">
            <p className="text-[#EFB342] uppercase tracking-[0.2em] text-xs font-semibold mb-4">The 10 daily GRIT actions</p>
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-white mb-4 leading-tight">
              Simple habits that <span className="text-[#EFB342]">support your menopause metabolism.</span>
            </h2>
            <p className="text-stone-200 text-sm md:text-base max-w-2xl mx-auto leading-relaxed">
              We gamify your metabolic health. Every single day, you&apos;ll log into your custom dashboard and track 10 crucial nutrition and lifestyle actions built specifically to protect your changing physiology.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 md:gap-4">
            {challengeActions.map((action) => {
              const isExpanded = expandedActions[action.id];
              return (
                <button
                  key={action.id}
                  onClick={() => toggleAction(action.id)}
                  className={`bg-[#F5F4F1] border border-stone-200 rounded-2xl p-4 text-center hover:border-[#EFB342] hover:shadow-sm transition-all cursor-pointer ${isExpanded ? 'col-span-2 md:col-span-5' : ''}`}
                  data-testid={`practice-${action.id}`}
                >
                  {isExpanded ? (
                    <div className="text-left">
                      <div className="flex items-start gap-4">
                        <span className="text-3xl sm:text-4xl">{action.emoji}</span>
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <h4 className="font-heading font-bold text-stone-900 text-lg sm:text-xl">{action.name}</h4>
                            <ChevronUp size={18} className="text-stone-500" />
                          </div>
                          <p className="text-stone-600 text-sm sm:text-base mt-2 leading-relaxed">{action.description}</p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <>
                      <span className="text-2xl sm:text-3xl mb-2 block">{action.emoji}</span>
                      <h4 className="font-heading font-semibold text-stone-900 text-sm sm:text-base mb-1 leading-tight">{action.name}</h4>
                      <div className="flex items-center justify-center gap-1 text-stone-400">
                        <span className="text-[11px]">Learn more</span>
                        <ChevronDown size={11} />
                      </div>
                    </>
                  )}
                </button>
              );
            })}
          </div>

          <p className="text-center mt-10 text-stone-300 text-sm md:text-base leading-relaxed max-w-3xl mx-auto">
            These aren&apos;t random &ldquo;healthy habits&rdquo; — every RxGRIT action is a targeted biological lever that addresses the specific consequences of declining estrogen in the body. Protein protects muscle mass and resting metabolic rate. Limiting refined sugar targets insulin resistance. Sleep reduces cortisol and regulates hunger cues.
          </p>
          <p className="text-center text-stone-300 text-sm md:text-base leading-relaxed max-w-3xl mx-auto mt-4">
            Earn <span className="text-white font-medium">1 point for every action taken every day</span>, climb the leaderboard, and build a metabolism that menopause can&apos;t derail.
          </p>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 px-4 bg-[#F5F4F1]">
        <div className="max-w-6xl mx-auto">
          <p className="text-[#EFB342] uppercase tracking-[0.2em] text-xs font-semibold text-center mb-4">Real GRIT Members</p>
          <h2 className="font-heading text-2xl md:text-4xl font-bold text-stone-900 text-center mb-14 leading-tight">
            Small habits. Real change.
          </h2>

          <div className="grid md:grid-cols-3 gap-6 md:gap-8">
            {testimonials.map((testimonial, index) => (
              <figure key={index} className="bg-[#F0EBE1] rounded-3xl p-8 hover:shadow-md transition-shadow" data-testid={`testimonial-${index}`}>
                <div className="flex gap-1 mb-5">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="text-[#EFB342] fill-[#EFB342]" size={16} />
                  ))}
                </div>
                <blockquote className="text-stone-800 mb-6 text-base md:text-lg leading-relaxed">
                  &quot;{testimonial.quote}&quot;
                </blockquote>
                <figcaption className="text-stone-600 font-semibold text-sm">— {testimonial.name}</figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* About Yvette — editorial magazine feature */}
      <section className="py-24 px-4 bg-stone-50">
        <div className="max-w-5xl mx-auto">
          <p className="text-[#EFB342] uppercase tracking-[0.2em] text-xs font-semibold text-center mb-10">The human behind the tech</p>
          <div className="bg-[#F5F4F1] rounded-3xl p-8 md:p-14 shadow-sm border border-stone-100">
            <div className="grid md:grid-cols-[auto,1fr] gap-10 md:gap-14 items-center">
              <div className="mx-auto md:mx-0 w-56 h-56 md:w-64 md:h-64 rounded-2xl overflow-hidden shadow-md">
                <img
                  src={PHOTOS.coach}
                  alt="Yvette Styner, RHN"
                  className="w-full h-full object-cover object-[50%_20%]"
                  style={{ filter: 'saturate(0.85) brightness(1.05) contrast(0.95)' }}
                  data-testid="coach-portrait"
                />
              </div>
              <div>
                <h3 className="font-heading text-2xl md:text-3xl font-bold text-stone-900 mb-2">Meet Yvette Styner, RHN</h3>
                <p className="text-stone-500 text-xs md:text-sm mb-6 leading-relaxed">
                  Registered Holistic Nutritionist™ · Menopause Metabolism Educator · Fellow Menopausal Woman
                </p>
                <p className="text-stone-700 text-sm md:text-base leading-relaxed mb-4">
                  I created RxGRIT because knowing what to do isn&apos;t the same as doing it — and menopause makes that gap even wider. This is the daily support I wish every woman had beside her.
                </p>
                <p className="text-stone-700 text-sm md:text-base leading-relaxed mb-6">
                  RxGRIT isn&apos;t a hands-off app. I&apos;m actively in there every day — answering questions, showing up in the community, and helping you stay accountable.
                </p>
                <p className="inline-block bg-[#EFB342]/20 text-stone-900 text-base md:text-lg font-semibold leading-relaxed px-3 py-1.5 rounded-md">
                  Like a menopause coach in your pocket!
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing CTA */}
      <section className="py-20 md:py-24 px-4 bg-[#F5F4F1]">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-12 gap-10 md:gap-14 items-center">
            {/* Left — value story */}
            <div className="md:col-span-7">
              <div className="inline-flex items-center gap-3 mb-8">
                <p className="text-[#EFB342] uppercase tracking-[0.2em] text-xs font-semibold">GRIT Membership</p>
                <span className="w-16 h-px bg-[#EFB342]"></span>
              </div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-stone-900 mb-5 leading-[1.05] uppercase tracking-tight">
                Support on your terms.
              </h2>
              <p className="text-stone-600 text-base md:text-lg mb-10 leading-relaxed max-w-lg">
                Because menopause deserves ongoing support — not another costly program you quit in three weeks.
              </p>

              <ul className="space-y-6 max-w-lg">
                {[
                  { icon: Calendar, title: 'USE IT WHEN YOU WANT', body: 'Pop in and out, play along with the daily challenge, explore the resources — whenever it works for you.' },
                  { icon: Trophy, title: 'STAY MOTIVATED', body: 'Earn points, track your progress and climb the leaderboard in a fun, supportive community.' },
                  { icon: BookOpen, title: 'GET THE TOOLS YOU NEED', body: 'Daily tips, recipes, and science-backed strategies — all in one place.' },
                  { icon: UserCheck, title: '1:1 COACHING WHEN YOU WANT IT', body: 'Members get 50% off any and all personal coaching sessions with Yvette if ever needed.' },
                ].map(({ icon: Icon, title, body }) => (
                  <li key={title} className="flex gap-4">
                    <div className="w-11 h-11 rounded-full bg-[#EFB342]/15 border border-[#EFB342]/25 flex items-center justify-center flex-shrink-0">
                      <Icon className="text-[#EFB342]" size={18} />
                    </div>
                    <div>
                      <p className="font-heading font-bold text-stone-900 text-sm md:text-base tracking-wide mb-1">{title}</p>
                      <p className="text-stone-600 text-xs md:text-sm leading-relaxed">{body}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            {/* Right — pricing card */}
            <div className="md:col-span-5">
              <div className="bg-stone-50 border border-stone-200 rounded-3xl p-8 md:p-10 shadow-xl md:shadow-2xl">
                <p className="text-stone-500 text-[11px] md:text-xs uppercase tracking-[0.2em] mb-4 font-semibold">Full RxGRIT Membership</p>
                <div className="flex items-baseline gap-2 mb-6 pb-6 border-b border-stone-200">
                  <span className="font-heading text-5xl md:text-6xl font-bold text-[#EFB342] leading-none">$200</span>
                  <span className="text-stone-500 text-base md:text-lg">/year</span>
                </div>

                <ul className="space-y-4 mb-8">
                  {[
                    'Full access to RxGRIT',
                    'Monthly challenges & leaderboard',
                    'Daily tips, recipes & resources',
                    'Supportive community',
                    '50% off personalized 1:1 coaching with Yvette',
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-3">
                      <span className="w-5 h-5 rounded-full bg-[#EFB342] flex items-center justify-center flex-shrink-0 mt-0.5">
                        <CheckCircle className="text-black" size={12} strokeWidth={3} />
                      </span>
                      <span className="text-stone-700 text-sm md:text-base leading-snug">{item}</span>
                    </li>
                  ))}
                </ul>

                <a
                  href="https://l.bttr.to/fXYvA"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 w-full bg-[#EFB342] text-black py-4 rounded-full font-semibold text-base md:text-lg hover:bg-[#D99F30] transition-all hover:-translate-y-0.5 hover:shadow-lg"
                  data-testid="pricing-cta"
                >
                  Join RxGRIT
                  <ArrowRight size={18} />
                </a>
                <p className="text-stone-500 text-xs md:text-sm text-center mt-4">Pay once · Access all year</p>
              </div>

              <p className="text-stone-600 text-sm md:text-base text-center mt-8">
                Still have questions?{' '}
                <a
                  href="https://p.bttr.to/3CG1F6b"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-stone-900 underline hover:text-[#EFB342] transition-colors font-medium"
                  data-testid="pricing-chat-link"
                >
                  Let&apos;s chat.
                </a>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-24 px-4 bg-stone-50">
        <div className="max-w-3xl mx-auto">
          <p className="text-[#EFB342] uppercase tracking-[0.2em] text-xs font-semibold text-center mb-4">Answers</p>
          <h2 className="font-heading text-2xl md:text-4xl font-bold text-stone-900 text-center mb-14 leading-tight">
            Frequently asked.
          </h2>

          <div className="space-y-3">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-[#F5F4F1] border border-stone-200 rounded-2xl overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full flex items-center justify-between p-6 text-left hover:bg-stone-50 transition-colors"
                  data-testid={`faq-toggle-${index}`}
                >
                  <span className="font-sans font-medium text-stone-900 text-sm md:text-base pr-4">{faq.question}</span>
                  {openFaq === index ? (
                    <ChevronUp className="text-[#EFB342] flex-shrink-0" size={20} />
                  ) : (
                    <ChevronDown className="text-stone-400 flex-shrink-0" size={20} />
                  )}
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-6">
                    <p className="text-stone-600 text-base md:text-lg leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 px-4 bg-[#F0EBE1]">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-[#EFB342] uppercase tracking-[0.2em] text-xs font-semibold mb-4">Your invitation</p>
          <h2 className="font-heading text-2xl md:text-4xl font-bold text-stone-900 mb-6 leading-tight">
            Understand what&apos;s happening. Know what to do about it.
          </h2>
          <p className="text-stone-700 text-sm md:text-base mb-8 max-w-xl mx-auto leading-relaxed">
            Menopause changes your biology. RxGRIT helps you understand what&apos;s happening, know what to do about it, and put the right strategies into practice — one day at a time.
          </p>
          <div className="mb-12 max-w-2xl mx-auto">
            <div className="relative bg-stone-900 rounded-2xl px-6 py-8 md:px-10 md:py-10 shadow-xl">
              <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#EFB342] text-black text-[10px] md:text-xs font-bold uppercase tracking-[0.25em] px-4 py-1 rounded-full">The bottom line</span>
              <p className="font-heading text-xl md:text-3xl font-bold text-white leading-[1.15]">
                Menopause really does <span className="text-[#EFB342]">change your biology.</span>
                <span className="block mt-2">Now you can do something about it.</span>
              </p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="https://l.bttr.to/fXYvA"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-[#EFB342] text-black px-8 py-4 rounded-full font-semibold text-base hover:bg-[#D99F30] transition-all hover:-translate-y-0.5 flex items-center gap-2 w-full sm:w-auto justify-center"
              data-testid="final-primary-cta"
            >
              Start Building Your GRIT
              <ArrowRight size={18} />
            </a>
            <Link
              to="/free-trial"
              className="bg-[#F5F4F1] border border-stone-300 text-stone-800 px-8 py-4 rounded-full font-semibold text-base hover:bg-stone-100 transition-all flex items-center gap-2 w-full sm:w-auto justify-center"
              data-testid="final-secondary-cta"
            >
              <Gift size={18} />
              Start free trial
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-10 px-4 bg-[#F5F4F1] border-t border-stone-200">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-[#EFB342] rounded-lg flex items-center justify-center">
              <span className="text-black font-heading font-bold text-sm">Rx</span>
            </div>
            <span className="text-stone-600 text-sm">RxGRIT by BUILD Nutrition</span>
          </div>
          <div className="flex items-center gap-6 text-sm text-stone-600">
            <Link to="/login" className="hover:text-stone-900 transition-colors">Member Login</Link>
            <Link to="/free-trial" className="hover:text-stone-900 transition-colors">Free Trial</Link>
            <a href="https://buildnutrition.ca" target="_blank" rel="noopener noreferrer" className="hover:text-stone-900 transition-colors">Main Website</a>
          </div>
          <p className="text-stone-500 text-xs">© {new Date().getFullYear()} BUILD Nutrition. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

/**
 * Pillar card — image top, editorial content below. Cards feel like magazine
 * feature cards, not app UI. Yellow badge overlay so brand still sings.
 */
const PillarCard = ({ image, imageAlt, title, description, bullets, imageStyle }) => {
  return (
    <div className="bg-[#F5F4F1] rounded-3xl overflow-hidden border border-stone-100 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 group">
      <div className="relative aspect-[5/4] overflow-hidden">
        <img src={image} alt={imageAlt} style={imageStyle} className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700" />
      </div>
      <div className="p-6 md:p-8">
        <h3 className="font-heading text-xl md:text-2xl font-bold text-stone-900 mb-3 leading-tight">{title}</h3>
        <p className="text-stone-600 text-base leading-relaxed mb-6">{description}</p>
        <ul className="space-y-2.5">
          {bullets.map(({ icon: BulletIcon, text }) => (
            <li key={text} className="flex items-center gap-3 text-sm text-stone-700">
              <BulletIcon className="text-[#EFB342] flex-shrink-0" size={14} />
              {text}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default SalesPage;
